import * as React from "react";
import classNames from "classnames";

import Breadcrumbs from "./Breadcrumbs";
import { SEO_URLS } from "./landing_utils";
import { Link, NewLinksSection } from "./LandingPageLinks";
import SearchDropdown, { SearchCategory } from "./SearchDropdown";
import { Footer, FooterSignUpSection, Header, PromoFeaturesSection } from "./shared";

// eslint-disable-next-line css-modules/no-unused-class
import sharedCss from "./shared.module.scss";
import css from "./TenderSearchPage.module.scss";

const BREADCRUMBS = [
  {
    id: "home",
    name: "Home",
    href: "/",
  },
  {
    id: "tenders",
    name: "Tenders",
  },
];

type Props = {
  app_root_url: string;
  buyers_links: Link[];
  sources_links: Link[];
  topics_links: Link[];
};

function TenderSearchPage({
  app_root_url,
  buyers_links,
  topics_links,
  sources_links,
}: Props): JSX.Element {
  const [searchCategory, setSearchCategory] = React.useState<SearchCategory>("buyer");

  return (
    <div className={css.landingPage}>
      <Header appRootUrl={app_root_url} />
      <main className={css.pageGrid}>
        <Breadcrumbs items={BREADCRUMBS} className={css.breadcrumbs} />
        <h1 className={classNames(css.header, css.textCentre)}>Find Tenders &amp; Contracts</h1>
        <p className={classNames(css.textCentre, css.body)}>
          Stotles centralises thousands of public sector opportunities into one platform, tailored
          to you. Search for opportunities by buyer, topic or source below.
        </p>
        <h2 className={classNames(css.subHeader, css.textCentre)}>
          Search for tenders and contracts by...
        </h2>
        <div className={css.tabs}>
          <button
            className={searchCategory === "buyer" ? css.activeTab : undefined}
            onClick={() => setSearchCategory("buyer")}
          >
            Buyer
          </button>
          <button
            className={searchCategory === "topic" ? css.activeTab : undefined}
            onClick={() => setSearchCategory("topic")}
          >
            Topic
          </button>
          <button
            className={searchCategory === "source" ? css.activeTab : undefined}
            onClick={() => setSearchCategory("source")}
          >
            Source
          </button>
        </div>
        <SearchDropdown category={searchCategory} />
        <div className={classNames(sharedCss.greenBox, css.upperPromoBox)}>
          <h2>Explore beyond tenders & contracts</h2>
          <div>
            <a
              href={app_root_url + SEO_URLS.signUp + "?utm_source=seo_lp"}
              className={classNames(sharedCss.greenButton, sharedCss.button, sharedCss.largeButton)}
              data-id="signup_cta"
            >
              Try for free
            </a>
            <a href="https://www.stotles.com/request-a-demo">Book a demo</a>
          </div>
        </div>
        <NewLinksSection
          title={
            <h4 className={css.linkspamTitle}>Top Buyers for Public Sector Contracts & Tenders</h4>
          }
          description={
            <p className={css.linkSpamDescription}>
              Stotles combines the procurement activity of thousands of public sector buyers into
              one platform. Explore open tenders, recent contract awards and upcoming contract
              expiries of relevant buyers below.
            </p>
          }
          links={buyers_links}
          className={css.linkSpam}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.buyerSearch}
                data-id="explore_articles"
              >
                Explore over 15,000 buyers
              </a>
            </div>
          }
        />
        <NewLinksSection
          title={<h4 className={css.linkspamTitle}>Top Topics - Find Tenders & Contracts UK</h4>}
          description={
            <p className={css.linkSpamDescription}>
              Stotles uses government procurement CPV codes to group millions of public sector
              opportunities into relevant topics for suppliers. Explore these topics below.
            </p>
          }
          links={topics_links}
          className={css.linkSpam}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.topicSearch}
                data-id="explore_articles"
              >
                Explore more than 2,000 topics
              </a>
            </div>
          }
        />
        <NewLinksSection
          title={<h4 className={css.linkspamTitle}>Top Sources - Find Tenders & Contracts UK</h4>}
          description={
            <p className={css.linkSpamDescription}>
              Stotles aggregates data from every major procurement data source in the UK. Explore
              public sector activity being published on these sources below.
            </p>
          }
          links={sources_links}
          className={css.linkSpam}
          footer={
            <div className={css.exploreMoreContainer}>
              <a
                className={css.exploreMoreText}
                href={SEO_URLS.sourceSearch}
                data-id="explore_articles"
              >
                Explore more sources
              </a>
            </div>
          }
        />
        <PromoFeaturesSection />
        <FooterSignUpSection appRootUrl={app_root_url} />
      </main>
      <Footer />
    </div>
  );
}

export default TenderSearchPage;
